import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import theme from '../../config/theme'
import Layout from '../../layout/layout'
import Input from '../../common/input'
import Table from '../../common/table'
import Pagination from '../../common/pagination'
import { IoArrowBack } from 'react-icons/io5'
import { SegmentUserDto } from '../../dtos/pcSegment.dto'
import Breadcrumb from '../../common/breadcrumb'
import { FaChevronLeft } from 'react-icons/fa'
import { segmentsApi } from '../../utils/apiClient'

const PCSegmentDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { segmentId } = useParams<{ segmentId?: string }>()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // API data states
  const [segmentUsers, setSegmentUsers] = useState<SegmentUserDto[]>([])
  const [segmentData, setSegmentData] = useState<{ segmentName: string } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch segment by ID
  const fetchSegment = useCallback(async () => {
    if (!segmentId) return

    setIsLoading(true)
    setError(null)

    try {
      const segment = await segmentsApi.getSegmentById(segmentId)
      setSegmentData({ segmentName: segment.segmentName })
      setSegmentUsers(segment.segmentUsers || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch segment details'
      setError(errorMessage)
      setSegmentUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [segmentId])

  // Fetch segment on mount
  useEffect(() => {
    fetchSegment()
  }, [fetchSegment])

  // Get segment name from API data or use default
  const displaySegmentName = segmentData?.segmentName || 'Unknown Segment'

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) {
      return segmentUsers
    }
    const lowerQuery = searchQuery.toLowerCase()
    return segmentUsers.filter((item: SegmentUserDto) =>
      (item.employeeName?.toLowerCase().includes(lowerQuery)) ||
      (item.employeeCode?.toLowerCase().includes(lowerQuery)) ||
      (item.branchName?.toLowerCase().includes(lowerQuery)) ||
      (item.lineUserId?.toLowerCase().includes(lowerQuery))
    )
  }, [searchQuery, segmentUsers])

  // Pagination calculations
  const totalItems = filteredData.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  // Map data to add id field for Table component key
  const paginatedData = filteredData.slice(startIndex, endIndex).map((item, index) => ({
    ...item,
    id: item.employeeCode || item.lineUserId || `user-${startIndex + index}` // Use employeeCode or lineUserId as unique key
  }))

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Table headers
  const header = ['Employee Name', 'Employee Code', 'Branch Name', 'LINE User ID']

  // Render table row
  const renderRow = (item: SegmentUserDto) => {
    return (
      <>
        {/* Employee Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden mr-3"
              style={{ backgroundColor: theme.color.light300 }}
            >
              {item.linePictureUrl ? (
                <img
                  src={item.linePictureUrl}
                  alt={item.employeeName || ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span
                  className="text-sm font-medium"
                  style={{ color: theme.color.gray400 }}
                >
                  {(item.employeeName || '').charAt(0)}
                </span>
              )}
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: theme.color.gray500 }}
            >
              {item.employeeName || '-'}
            </span>
          </div>
        </td>
        {/* Employee Code */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.employeeCode || '-'}
          </span>
        </td>
        {/* Branch Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.branchName || '-'}
          </span>
        </td>
        {/* LINE User ID */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.lineUserId || '-'}
          </span>
        </td>
      </>
    )
  }

  return (
    <Layout currentPath="/pc-segment">
      <div className="flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/pc-segment')}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm border transition-colors hover:bg-gray-50"
            style={{
              borderColor: theme.color.light300,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            {React.createElement(FaChevronLeft as any, {
              className: "w-3 h-3",
              style: { color: theme.color.gray500 }
            })}
          </button>
          <div className="ml-3">
            <Breadcrumb
              items={[
                { label: 'PC Segments', onClick: () => navigate('/pc-segment') },
                { label: displaySegmentName }
              ]}
            />
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative" style={{ width: '100%' }}>
            <Input
              type="text"
              placeholder="Search by PC name, branch, or PC code..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setCurrentPage(1) // Reset to first page when searching
              }}
              className="!pl-12"
            />
            <div
              className="absolute left-4"
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.color.gray200,
                pointerEvents: 'none'
              }}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.color.gray500 }}></div>
          </div>
        ) : (
          /* Table Container */
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="max-h-[calc(100vh-295px)] flex flex-col relative">
              {/* Table */}
              <Table
                data={paginatedData}
                className={`flex-1 ${totalPages > 1 ? 'pb-0' : ''}`}
                header={header}
                renderRow={renderRow}
              />

              {/* Pagination - Fixed at bottom */}
              {!isLoading && totalPages > 0 && (
                <div className="fixed bottom-0 left-64 right-0 mx-6 py-4 z-10">
                  <div className="flex justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemsPerPage={itemsPerPage}
                      totalItems={totalItems}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default PCSegmentDetailPage

